import { queryOptions, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient, unwrap } from "../api/client";
import type { components } from "../api/generated/vision-api";
import { formatDateTime } from "../i18n/format";

export type ServerRole = components["schemas"]["ServerRole"];

/**
 * The server's role (VisionPlatform server-role SR-42) from `GET /v1/meta`, read once a session by the shell the
 * person signed in to. The development bar sits above both shells, outside sign-in, so it only reads what a shell
 * fetched under this key and never fetches itself.
 */
const ROLE_KEY = ["/v1/meta", "server-role"] as const;

export const serverRoleQuery = (realm: "customers" | "ops") =>
  queryOptions({
    queryKey: ROLE_KEY,
    queryFn: async (): Promise<ServerRole | null> =>
      (await unwrap(apiClient(realm).GET("/v1/meta", {}))).server_role ?? null,
    staleTime: Infinity,
    retry: false,
  });

/** The role a shell read, or undefined before then (and on a server older than vision-api 0.53.0). */
export function useKnownServerRole(): ServerRole | undefined {
  const q = useQuery({ queryKey: ROLE_KEY, queryFn: () => null as ServerRole | null, enabled: false });
  return q.data ?? undefined;
}

/**
 * Virtual devices are created and extended only on a develop server (server-role SR-38): false on production, true
 * on develop and while the role is unknown (the server refuses anyway).
 */
export function useVirtualCreateAllowed(): boolean {
  return useKnownServerRole() !== "production";
}

const ROLE_STYLE: Record<ServerRole, string> = {
  production: "border-gray-700 bg-gray-800 text-white",
  develop: "border-violet-800 bg-violet-800 text-white",
};

/**
 * The read-only role badge (SR-40, SR-41): "Production" in dark grey, "Develop" in the development bar's purple. The
 * tooltip says it cannot be changed here.
 */
export function ServerRoleBadge({ role, since }: { role: ServerRole; since?: string }) {
  const { t, i18n } = useTranslation();
  const title = since
    ? t("environment.role.titleSince", { date: formatDateTime(since, i18n.language) })
    : t("environment.role.title");
  return (
    <span
      data-server-role={role}
      title={title}
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${ROLE_STYLE[role]}`}
    >
      {t(`environment.role.${role}`)}
    </span>
  );
}
